import moment from "moment";

import { type ClientSetting } from "@features/mmm/components/explore-budgets/client-setting/types";
import { ActiveTab } from "@features/mmm/hooks/mmm-url-state";
import {
  type MMMClientCycleBase,
  MMMClientState,
  type MMMClientCycle,
} from "@north-beam/nb-common";

const MODEL_INSIGHTS_ENABLED_TAB_STATES: { [K in MMMClientState]?: true } = {
  [MMMClientState.PREPARING_MIX]: true,
  [MMMClientState.CREATING_SETTINGS]: true,
  [MMMClientState.ADOPTING_MIX]: true,
  [MMMClientState.TRACKING_MIX]: true,
  [MMMClientState.REVIEWING_PERIOD]: true,
};

const PERFORMANCE_TRACKING_ENABLED_TAB_STATES: {
  [K in MMMClientState]?: true;
} = {
  [MMMClientState.TRACKING_MIX]: true,
  [MMMClientState.REVIEWING_PERIOD]: true,
};

const SCENARIO_MODELLING_ENABLED_TAB_STATES: {
  [K in MMMClientState]?: true;
} = {
  [MMMClientState.ADOPTING_MIX]: true,
  [MMMClientState.TRACKING_MIX]: true,
  [MMMClientState.REVIEWING_PERIOD]: true,
};

const ADMIN_SCENARIO_MODELLING_ENABLED_TAB_STATES: {
  [K in MMMClientState]?: true;
} = {
  [MMMClientState.PREPARING_MIX]: true,
  [MMMClientState.ADOPTING_MIX]: true,
  [MMMClientState.TRACKING_MIX]: true,
  [MMMClientState.REVIEWING_PERIOD]: true,
};

const SUMMARY_TAB_STATES: {
  [K in MMMClientState]?: true;
} = {
  [MMMClientState.REVIEWING_PERIOD]: true,
};

export const isModelInsightsTabEnabled = (
  clientCycle: MMMClientCycle,
  clientSettings: ClientSetting[],
): boolean => {
  if (MODEL_INSIGHTS_ENABLED_TAB_STATES[clientCycle.state]) {
    if (clientCycle.state === MMMClientState.CREATING_SETTINGS) {
      return clientSettings.length > 0;
    }

    return true;
  }
  return false;
};

export const isPerformanceTrackingTabEnabled = (
  clientCycle: MMMClientCycle,
  hasAdoptedMix: boolean,
): boolean => {
  return (
    hasAdoptedMix &&
    !!PERFORMANCE_TRACKING_ENABLED_TAB_STATES[clientCycle.state]
  );
};

export const isScenarioModellingEnabled = (
  clientCycle: MMMClientCycle | MMMClientCycleBase,
  isAdmin: boolean,
): boolean =>
  isAdmin
    ? !!ADMIN_SCENARIO_MODELLING_ENABLED_TAB_STATES[clientCycle.state]
    : !!SCENARIO_MODELLING_ENABLED_TAB_STATES[clientCycle.state];

export const isSummaryEnabled = (
  clientCycle: MMMClientCycle,
  timezone: string,
): boolean => {
  return (
    !!SUMMARY_TAB_STATES[clientCycle.state] ||
    moment.tz(timezone).format("YYYY-MM-DD") > clientCycle.date_range.end_date
  );
};

export const isValidActiveTab = (
  tab: ActiveTab,
  cycle: MMMClientCycle,
  clientSettings: ClientSetting[],
  isAdmin: boolean,
  hasAdoptedMix: boolean,
  timezone: string,
): boolean => {
  switch (tab) {
    case ActiveTab.EXPLORE_MIXES:
      return true;
    case ActiveTab.MODEL_INSIGHTS:
      return isModelInsightsTabEnabled(cycle, clientSettings);
    case ActiveTab.PERFORMANCE_TRACKING:
      return isPerformanceTrackingTabEnabled(cycle, hasAdoptedMix);
    case ActiveTab.SUMMARY:
      return isSummaryEnabled(cycle, timezone);
  }

  return false;
};
